import { ReactComponent as SearchSVG } from '@assets/SVGs/PresenteeismTable/SearchSVG.svg';
import Image from '@components/General/Image';
import { TableCell } from '@mui/material';
import React, { useEffect } from 'react';
import styles from '@styles/screens/StartDemoScreen.module.css'
import { ReactComponent as QontactTextSVG } from '@assets/SVGs/GeneralUse/Qontact.svg';
import { HorizontalContainer, HorizontalSpacer, VerticalContainer, VerticalSpacer } from "@components/General/Containers"
import designSystem from "@styles/designSystem"
import { H2Title, Subtitle } from '@components/General/Titles';
import Table from '@components/General/Table';
import { useCheckinsViewModel, ViewState } from '../view-models/CheckinsScreenViewModel';
import { TextInput } from '@components/General/TextInput';
import { GhostButton, RefreshButton } from '@components/General/Button';
import { Modal } from '@components/Messages/Modal';
import { usePopup } from '@contexts/popupContext';
import { PromotionalScreen } from './PromotionalScreen';
import { ErrorText } from '@components/General/Texts';
import { QRModalScreen } from './QRModalScreen';

function rowTemplate(_index, row) {

    const getColor = (color) => {
        switch (color) {
            case 'green':
                return designSystem.colorPresenteeismGreen
            case 'red':
                return designSystem.colorPresenteeismRed
            case 'yellow':
                return designSystem.colorPresenteeismYellow
            default:
                return designSystem.colorPresenteeismDefault
        }
    }

    const processName = (name) => {
        const words = name?.split(' ');
        if (!words) {
            return name
        }
        const processedWords = words.map(word => {
            const firstLetter = word.charAt(0).toUpperCase();
            const restOfWord = word.slice(1).toLowerCase();
            return firstLetter + restOfWord;
        });
        return processedWords.join(' ');
    }

    return (
        <React.Fragment>
            <TableCell component="th" scope="row" align='left'>
                <div className={styles['circle-mask']} style={{ width: '24px', height: '24px', marginRight: '10px', display: 'inline-block', verticalAlign: 'top' }}>
                    <Image src={row.photo || '/assets/Q.png'} alt="Profile Picture" isRound={true} style={{ width: '100%', height: '100%' }} />
                </div>
                <div style={{ display: 'inline-block', maxWidth: 'calc(100% - 34px)', color: row.deactivated ? "gray" : '#101828', fontWeight: 'bold' }}>
                    {processName(row.colaborador)}
                </div>
            </TableCell>
            <TableCell align="center" className={styles['table-row']}>{row.date}</TableCell>
            <TableCell align="center" className={styles['table-row']}>{row.checkinTime}</TableCell>
            <TableCell align="center" className={styles['table-row']}>{row.checkoutTime}</TableCell>
            <TableCell align="left" className={styles['table-row']}>
                <div style={{ display: 'flex', paddingLeft: '20px', flexDirection: 'column' }}>

                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
                            <circle cx="4" cy="4" r="3" fill={getColor(row.analysis?.color)} />
                        </svg>
                        <div className={styles['analisis-text']} style={{ paddingLeft: '5px' }}>
                            {row.analysis?.hsTrabajadasText}
                        </div>
                    </div>
                    <div style={{ display: 'flex' }}>
                        <div style={{ width: '8px', height: '8px', color: '#888693' }}>
                            {row.analysis?.sign}
                        </div>
                        <div className={styles['shift-text']} style={{ paddingLeft: '5px' }}>
                            {row.analysis?.subtext}
                        </div>
                    </div>

                </div>
            </TableCell>
            <TableCell align="center" className={styles['table-row']}>{row.edificio}</TableCell>
            <TableCell />
        </React.Fragment>
    );
}


export const CheckinsScreen: React.FC = () => {

    const columns = [
        {
            label: 'Colaborador'.localized(),
            dataKey: 'colaborador',
            style: {
                textAlign: 'left',
                paddingLeft: designSystem.paddingLg
            }
        },
        {
            label: 'Fecha'.localized(),
            dataKey: 'fecha',
            size: '12.5%',
        },
        {
            label: 'Ingreso'.localized(),
            dataKey: 'ingreso',
            size: '10%',
        },
        {
            label: 'Egreso'.localized(),
            dataKey: 'egreso',
            size: '10%',
        },
        {
            label: 'Análisis'.localized(),
            dataKey: 'analisis',
        },
        {
            label: 'Edificio'.localized(),
            dataKey: 'edificio',
            size: '18%',
        },
        {
            label: '',
            dataKey: 'info',
            size: '10px'
        }
    ];

    const {
        visiblePresenteeismRecords,
        viewState,
        searchText,
        placeholderText,
        qpID,
        serviceError,
        setSearchText,
        setPlaceholderText,
        reloadData,
        search,
        viewDidAppear,
        analytics,
    } = useCheckinsViewModel()

    // viewDidAppear - runs only once when component first mounts
    useEffect(() => {
        // Call your hook or function here
        viewDidAppear()
    }, []) // Empty dependency array ensures this runs only once
    
    const { showPopup, hidePopup } = usePopup()

    const handleOpenModal = () => {
        analytics.trackPromotionalModalButtonTapped()
        showPopup(() => (
            <Modal 
                isOpen={true}
                onClose={hidePopup}
                width={'500px'}
                height={'100%'}
                maxHeight={'300px'}
            >
                <PromotionalScreen
                    onClick={() => {
                        analytics.trackWhatsAppButtonTapped()
                    }}
                />
            </Modal>
        ), true)
    }

    const handleOpenQRModal = () => {
        analytics.trackQRCodeButtonTapped()
        showPopup(() => (
            <Modal 
                isOpen={true}
                onClose={hidePopup}
                width={'400px'}
                height={'100%'}
                maxHeight={'550px'}
            >
                <VerticalContainer
                    alignment={'center'}
                >
                    <QRModalScreen qpID={qpID}/>
                    <GhostButton
                        text={'Cerrar'.localized()}
                        onClick={()=> {
                            hidePopup()
                        }}
                        width={'300px'}
                    />
                </VerticalContainer>
            </Modal>
        ), true)
    }

    return (
        <VerticalContainer
            gap={designSystem.gapNone}
            height={'100%'}
        >
            <HorizontalContainer backgroundColor={designSystem.colorMainAction} height={'100px'}>
                <div style={{
                    width: '200px',
                    height: '100%',
                    alignContent: 'start',
                    paddingLeft: designSystem.paddingLg,
                    zIndex: '-moz-initial',
                }}>
                    <a 
                        href="https://qontact.com.ar" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{
                            display: 'block',
                            width: '100%',
                            height: '100%',
                            cursor: 'pointer'
                        }}
                    >
                        <QontactTextSVG />
                    </a>
                </div>
            </HorizontalContainer>
            <VerticalContainer
                backgroundColor={designSystem.colorBackgroundGray3}
                width={'100%'}
                height={'100%'}
                maxWidth={'100%'}
                paddingBottom={designSystem.paddingMd}
                // alignment={'start'}
                paddingLeft={designSystem.paddingLgScreen}
                paddingRight={designSystem.paddingLgScreen}
            >
                <VerticalContainer gap={designSystem.paddingZero}>
                    <H2Title text={"Fichadas Hoy".localized()} />
                    <Subtitle text={"Presentismo de todos los colaboradores".localized()} />
                </VerticalContainer>
                <HorizontalContainer>
                    <TextInput
                        label=""
                        placeholder=""
                        onChange={(e) => { search(e) }}
                        onEnterPress={() => { }}
                        value={searchText}
                        width={'400px'}
                        icon={<SearchSVG style={{ width: designSystem.sizeIconWidth, height: designSystem.sizeIconHeight }} />}
                    />
                    <RefreshButton
                        text=""
                        onClick={reloadData}
                    />
                    { viewState == ViewState.error && 
                        <ErrorText text={serviceError}/>
                    }
                    <HorizontalContainer
                        alignment={'end'}
                        justifyContent={designSystem.justifyContentRight}
                    >
                        <GhostButton
                            text={'Escanea el QR'.localized()}
                            onClick={()=> {
                                handleOpenQRModal()
                            }}
                            width={'300px'}
                        />
                        <GhostButton
                            text={'Comienza tu prueba gratis!'.localized()}
                            onClick={()=> {
                                handleOpenModal()
                            }}
                            width={'300px'}
                        />
                    </HorizontalContainer>
                </HorizontalContainer>
                <Table
                    loading={viewState == ViewState.loading}
                    dataForRows={
                        visiblePresenteeismRecords
                    } columns={columns}
                    rowTemplate={rowTemplate}
                    placeHolder={placeholderText}
                />
            </VerticalContainer>
        </VerticalContainer>
    )
}
